import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'

export type NewCustomerMode = 'create' | 'edit'

export interface NewCustomerInitialValues {
  fullName: string
  externalId?: string

  contactAddress?: string
  contactEmail?: string
  contactPhone?: string

  invoiceName?: string
  invoiceAddress?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceVat?: string
  externalCustomerId?: string
}

export interface NewCustomerScreenProps {
  mode?: NewCustomerMode
  initialValues?: NewCustomerInitialValues
  showHeader?: boolean
  errorMessage?: string | null
  onSave?: (values: NewCustomerInitialValues) => void | Promise<void>
  onCancel?: () => void
}

const defaultInitialValues: NewCustomerInitialValues = {
  fullName: '',
}

function NewCustomerFormSections(props: {
  initial: NewCustomerInitialValues
}) {
  const { initial } = props

  return (
    <div className="space-y-6 text-xs">
      <div className="space-y-3">
        <FormCapsLabel>Customer</FormCapsLabel>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Name
          </FormFieldLabel>
          <UnderlineInput
            name="fullName"
            className="w-auto flex-1"
            placeholder="Customer full name"
            defaultValue={initial.fullName}
          />
        </div>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Omni customer ID
          </FormFieldLabel>
          <UnderlineInput
            name="externalId"
            className="w-auto flex-1"
            placeholder="e.g. CUST-12345"
            defaultValue={initial.externalId ?? ''}
          />
        </div>
      </div>

      <div className="space-y-3">
        <FormCapsLabel>Contact</FormCapsLabel>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Email
          </FormFieldLabel>
          <UnderlineInput
            name="contactEmail"
            className="w-auto flex-1"
            type="email"
            placeholder="Primary contact email"
            defaultValue={initial.contactEmail ?? ''}
          />
        </div>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Phone
          </FormFieldLabel>
          <UnderlineInput
            name="contactPhone"
            className="w-auto flex-1"
            type="tel"
            placeholder="Primary contact phone"
            defaultValue={initial.contactPhone ?? ''}
          />
        </div>
      </div>

      <div className="space-y-3">
        <FormCapsLabel>Address</FormCapsLabel>
        <div className="flex items-start gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Street address
          </FormFieldLabel>
          <UnderlineTextarea
            name="contactAddress"
            className="w-auto flex-1"
            rows={2}
            placeholder="Street and suburb"
            defaultValue={initial.contactAddress ?? ''}
          />
        </div>
      </div>

      <div className="space-y-3">
        <FormCapsLabel>Invoice</FormCapsLabel>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Invoice to
          </FormFieldLabel>
          <UnderlineInput
            name="invoiceName"
            className="w-auto flex-1"
            placeholder="Who to invoice"
            defaultValue={initial.invoiceName ?? initial.fullName}
          />
        </div>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Invoice email
          </FormFieldLabel>
          <UnderlineInput
            name="invoiceEmail"
            className="w-auto flex-1"
            type="email"
            placeholder="Invoice contact email"
            defaultValue={
              initial.invoiceEmail ?? initial.contactEmail ?? ''
            }
          />
        </div>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Invoice phone
          </FormFieldLabel>
          <UnderlineInput
            name="invoicePhone"
            className="w-auto flex-1"
            type="tel"
            placeholder="Invoice contact phone"
            defaultValue={
              initial.invoicePhone ?? initial.contactPhone ?? ''
            }
          />
        </div>
        <div className="flex items-start gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            Invoice address
          </FormFieldLabel>
          <UnderlineTextarea
            name="invoiceAddress"
            className="w-auto flex-1"
            rows={2}
            placeholder="Use customer address by default"
            defaultValue={
              initial.invoiceAddress ??
              initial.contactAddress ??
              ''
            }
          />
        </div>
        <div className="flex items-end gap-3">
          <FormFieldLabel className="min-w-[7rem]">
            VAT (optional)
          </FormFieldLabel>
          <UnderlineInput
            name="invoiceVat"
            className="w-auto flex-1"
            placeholder="VAT number"
            defaultValue={initial.invoiceVat ?? ''}
          />
        </div>
      </div>
    </div>
  )
}

export function NewCustomerScreen(props?: NewCustomerScreenProps) {
  const mode = props?.mode ?? 'create'
  const isEdit = mode === 'edit'
  const initial = props?.initialValues ?? defaultInitialValues
  const showHeader = props?.showHeader ?? true
  const errorMessage = props?.errorMessage ?? null

  const errorNotice = errorMessage ? (
    <p role="alert" className="text-xs text-red-600 whitespace-pre-line">
      {errorMessage}
    </p>
  ) : null

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (
    event,
  ) => {
    event.preventDefault()
    if (!props?.onSave) return

    const formData = new FormData(event.currentTarget)

    const values: NewCustomerInitialValues = {
      fullName: String(formData.get('fullName') ?? ''),
      externalId:
        (formData.get('externalId') as string) || undefined,
      contactAddress:
        (formData.get('contactAddress') as string) || undefined,
      contactEmail:
        (formData.get('contactEmail') as string) || undefined,
      contactPhone:
        (formData.get('contactPhone') as string) || undefined,
      invoiceName:
        (formData.get('invoiceName') as string) || undefined,
      invoiceAddress:
        (formData.get('invoiceAddress') as string) || undefined,
      invoiceEmail:
        (formData.get('invoiceEmail') as string) || undefined,
      invoicePhone:
        (formData.get('invoicePhone') as string) || undefined,
      invoiceVat: (formData.get('invoiceVat') as string) || undefined,
      externalCustomerId:
        (formData.get('externalCustomerId') as string) || undefined,
    }

    props.onSave(values)
  }

  const actions = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
        onClick={props?.onCancel}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        {isEdit ? 'Save changes' : 'Save customer'}
      </button>
    </div>
  )

  if (!showHeader) {
    return (
      <div className="bg-background p-6 text-ink">
        <form
          className="flex flex-col gap-6"
          onSubmit={handleSubmit}
        >
          <NewCustomerFormSections initial={initial} />
          {errorNotice}
          {actions}
        </form>
      </div>
    )
  }

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">
            {isEdit ? 'Edit Customer' : 'New Customer'}
          </h1>
          <p className="text-sm text-slate-600">
            {isEdit
              ? 'Update the default contact and invoicing details for this customer.'
              : 'Capture a new customer, including contact and invoicing details to reuse for future services.'}
          </p>
        </header>

        <form
          className="flex flex-col gap-6"
          onSubmit={handleSubmit}
        >
          <div className="rounded-md bg-background p-6 text-ink shadow-sm">
            <NewCustomerFormSections initial={initial} />
          </div>

          {errorNotice}
          {actions}
        </form>
      </main>
    </div>
  )
}
